<template>
    <aside class="cv3-cart">
        <header class="cv3-cart-head">
            <div class="cv3-cart-title">
                <h2>{{ $t('checkout.v3.cart_title') }}</h2>
                <span class="cv3-count" v-show="false">{{ store.count }}</span>
            </div>
            <button
                type="button"
                class="cv3-note-global-icon"
                :class="{ filled: !!store.orderComment }"
                @click="openOrderNote"
                :title="$t('checkout.v3.global_note_placeholder')"
            >
                <MessageCircleIcon :size="17" />
            </button>
        </header>

        <!-- Vente clôturée = document fiscal signé (NF525) : lecture seule, aucune modification possible.
             Le serveur refuse déjà toute écriture (OrderItemObserver), on retire juste les affordances. -->
        <div v-if="locked" class="cv3-locked-banner">
            <LockIcon :size="15" />
            <span>{{ $t('checkout.v3.closed_readonly') }}</span>
        </div>

        <div class="cv3-cart-body">
            <p v-if="!store.lines.length && !store.drafts.length" class="cv3-cart-empty">{{ $t('checkout.v3.empty_cart') }}</p>

            <!-- Voucher drafts (gift cards being issued) -->
            <div v-for="d in store.drafts" :key="'draft-' + d.voucherItemId" class="cv3-line cv3-draft">
                <div class="cv3-line-top">
                    <span class="cv3-line-icon draft"><GiftIcon :size="16" /></span>
                    <div class="cv3-line-info">
                        <span class="cv3-line-name"
                            >{{ draftLabel(d) }} {{ $t('common.of') }} {{ formatPrice(draftUnit(d)) }} €</span
                        >
                        <span class="cv3-draft-tag">{{ draftLabel(d) }}</span>
                    </div>
                    <button v-if="!locked" type="button" class="cv3-line-remove" :disabled="store.busy" @click="removeDraft(d)">
                        <Trash2Icon :size="16" />
                    </button>
                </div>
                <div class="cv3-line-bottom">
                    <div class="cv3-price-input">
                        <input
                            type="number"
                            step="0.01"
                            :value="draftUnit(d)"
                            :disabled="store.busy || locked"
                            @change="onDraftPrice(d, $event)"
                        />
                        <span class="cv3-cur">€</span>
                    </div>
                    <QuantityStepper
                        :value="Number(d.qty)"
                        :disabled="store.busy || locked"
                        @inc="store.draftInc(d.voucherItemId)"
                        @dec="store.draftDesc(d.voucherItemId)"
                        @change="onDraftQty(d, $event)"
                    />
                </div>
                <div v-if="Number(d.qty) > 1" class="cv3-draft-linetotal">
                    <span>{{ $t('checkout.v3.line_total') }}</span>
                    <span>{{ formatPrice(d.amount) }} €</span>
                </div>
                <!-- Validation button inside the gift card card -->
                <button v-if="!locked" type="button" class="cv3-validate-bc" @click="voucherModalOpen = true">
                    <GiftIcon :size="15" />
                    {{ $t(d.isUndatedTicket ? 'checkout.voucher.validate_undated' : 'checkout.voucher.validate_gift') }}
                </button>
            </div>

            <div v-for="line in store.lines" :key="line.rowId" class="cv3-line" :class="{ 'cv3-line-child': !!line.parent_id }">
                <div class="cv3-line-top">
                    <span class="cv3-line-icon">
                        <WalletIcon
                            v-if="
                                line.item_type &&
                                (line.item_type.includes('Transaction') ||
                                    line.item_type.includes('Wallet') ||
                                    line.name === 'Wallet')
                            "
                            :size="16"
                        />
                        <PackageIcon v-else :size="16" />
                    </span>
                    <div class="cv3-line-info">
                        <span class="cv3-line-name">{{ line.name }}</span>
                        <!-- Ligne bowling sèche : durée EFFECTIVE de la partie (palier — 6 joueurs
                             → 2 h sur une formule « 1 h »). Donnée serveur (more_details.duration),
                             rafraîchie à chaque changement d'effectif en même temps que le prix. -->
                        <span v-if="line.durationMinutes" class="cv3-line-duration">
                            <ClockIcon :size="12" /> {{ formatDuration(line.durationMinutes) }}
                        </span>
                        <button
                            v-if="!locked || line.comment"
                            type="button"
                            class="cv3-line-note"
                            :class="{ filled: !!line.comment }"
                            :disabled="locked"
                            @click="openLineNote(line)"
                        >
                            <MessageCircleIcon :size="13" />
                            <span class="cv3-note-text">{{ line.comment || $t('checkout.v3.add_note') }}</span>
                        </button>
                    </div>
                    <!-- A line tied to a booking/timeslot is NOT deletable from the cart (legacy Cart.vue
                         `v-if="!item.bookingId"`; VspService::deleteItem also refuses it server-side).
                         Same for every line once the sale is clôturée (NF525). -->
                    <!-- Le parent d'un billet simple n'est pas supprimable directement : il disparaît
                         quand sa dernière option (enfant) est retirée (VspService le refuse aussi). -->
                    <!-- Dernière ligne de la vente (drapeau serveur `isLastLine`) : désactivée, une
                         commande ne reste jamais sans article — on supprime la vente entière. -->
                    <button
                        v-if="!line.bookingId && !locked && !line.isSimpleTicketParent"
                        type="button"
                        class="cv3-line-remove"
                        :disabled="store.busy || !!line.isLastLine"
                        :title="line.isLastLine ? $t('checkout.v3.last_line_tip') : null"
                        @click="remove(line)"
                    >
                        <Trash2Icon :size="16" />
                    </button>
                    <span
                        v-else
                        class="cv3-line-locked"
                        :title="locked ? $t('checkout.v3.closed_readonly') : $t('checkout.v3.locked_line_tip')"
                    >
                        <LockIcon :size="15" />
                    </span>
                </div>
                <div class="cv3-line-bottom">
                    <div class="cv3-price-input">
                        <input
                            type="number"
                            step="0.01"
                            :value="line.price"
                            :disabled="store.busy || locked || line.isSimpleTicketParent"
                            @change="onPrice(line, $event)"
                        />
                        <span class="cv3-cur">€</span>
                    </div>
                    <!-- A line tied to a booking/time slot has a fixed quantity (reserved slot).
                         Editing it is refused server-side (VspService quantity guards), so lock the
                         +/- stepper here too, mirroring the delete lock above. -->
                    <!-- Une ligne bowling RÉSERVÉE (drapeau serveur `qtyLocked`) garde son effectif
                         fixe : il vit dans la réservation. Une ligne SÈCHE, elle, se règle ici par
                         re-résolution serveur (updateBookingQty) : ± naviguent la liste des effectifs
                         vendables (`bowlingSizes`, comme le popover d'ajout — jamais un compteur
                         libre, les paliers ne sont pas linéaires), la saisie tapée part telle
                         quelle — le serveur tranche (bornes, palier, prix, durée). -->
                    <!-- Un bon cadeau validé vaut UNE unité : sa quantité est dérivée du bon et
                         chaque unité porte son propre code. Le serveur la refuse déjà
                         (voucher_quantity_locked) ; on évite juste au caissier un aller-retour. -->
                    <QuantityStepper
                        :value="Number(line.qty)"
                        :disabled="
                            store.busy ||
                            !!line.isBookingTied ||
                            !!line.qtyLocked ||
                            locked ||
                            line.isSimpleTicketParent ||
                            isVoucherLine(line)
                        "
                        @inc="line.bowlingSizes ? onBowlingStep(line, 1) : store.incQty(line.rowId)"
                        @dec="line.bowlingSizes ? onBowlingStep(line, -1) : store.decQty(line.rowId)"
                        @change="line.bowlingSizes ? onBowlingQty(line, $event) : onQty(line, $event)"
                    />
                </div>

                <!-- Promotion persistée sur la ligne (promotions_orders.real_amount). Le champ prix
                     ci-dessus reste sur le prix BRUT (il alimente changePrice) : la remise s'affiche ici,
                     en lecture seule — ancien prix barré → nouveau prix. -->
                <div v-if="line.hasDiscount && store.promotion" class="cv3-line-discount">
                    <span class="cv3-line-promo" :title="store.promotion.title">
                        <TagIcon :size="12" /> {{ promoDisplay(store.promotion) }} · −{{ formatPrice(line.lineDiscount) }} €
                    </span>
                    <span class="cv3-line-prices">
                        <s class="cv3-line-oldprice" :title="$t('checkout.v3.old_price_label')"
                            >{{ formatPrice(line.price) }} €</s
                        >
                        <span class="cv3-line-newprice">{{ formatPrice(line.unitNet) }} €</span>
                    </span>
                </div>
                <div v-if="line.hasDiscount && Number(line.qty) > 1" class="cv3-draft-linetotal">
                    <span>{{ $t('checkout.v3.line_total') }}</span>
                    <span>
                        <s class="cv3-line-oldprice">{{ formatPrice(line.lineGross) }} €</s>
                        <span class="cv3-line-newprice">{{ formatPrice(line.lineNet) }} €</span>
                    </span>
                </div>

                <!-- Vendeur / Conseiller par ligne (legacy editSeller → OrderItem.id_seller / id_advised). -->
                <div v-if="false && store.staffs.length" class="cv3-line-staff">
                    <div class="cv3-staff-field">
                        <span class="cv3-staff-label"><UserIcon :size="12" /> {{ $t('checkout.v3.seller_label') }}</span>
                        <select :value="line.seller_id || ''" :disabled="store.busy" @change="onSeller(line, $event)">
                            <option value="">— {{ $t('checkout.v3.none') }} —</option>
                            <option v-for="s in store.staffs" :key="'sel' + s.id" :value="s.id">{{ staffName(s) }}</option>
                        </select>
                    </div>
                    <div class="cv3-staff-field">
                        <span class="cv3-staff-label"><UserCheckIcon :size="12" /> {{ $t('checkout.v3.adviser_label') }}</span>
                        <select :value="line.advised_id || ''" :disabled="store.busy" @change="onAdvised(line, $event)">
                            <option value="">— {{ $t('checkout.v3.none') }} —</option>
                            <option v-for="s in store.staffs" :key="'adv' + s.id" :value="s.id">{{ staffName(s) }}</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <footer class="cv3-cart-foot">
            <!-- Avec une promo persistée, on détaille brut → remise → net. Sans promo,
                 totalNet === total, donc la ligne TOTAL TTC reste seule, inchangée. -->
            <template v-if="store.discountTotal > 0">
                <div class="cv3-subtotal-row">
                    <span>{{ $t('checkout.v3.subtotal_label') }}</span>
                    <span>{{ formatPrice(store.total) }} €</span>
                </div>
                <div class="cv3-remise-row">
                    <span>
                        {{ $t('checkout.v3.discount_label') }}
                        <template v-if="store.promotion">({{ promoDisplay(store.promotion) }})</template>
                    </span>
                    <span>−{{ formatPrice(store.discountTotal) }} €</span>
                </div>
            </template>
            <div class="cv3-total-row">
                <span class="cv3-total-label">{{ $t('checkout.v3.total_ttc') }}</span>
                <span class="cv3-total-val">{{ formatPrice(netTotal) }} €</span>
            </div>
            <div v-if="store.currentPrice > 0" class="cv3-deposit-row">
                <div class="cv3-deposit-card cv3-deposit-paid">
                    <span class="cv3-deposit-label">{{ $t('checkout.v3.already_paid') || 'Déjà payé' }}</span>
                    <span class="cv3-deposit-amount">{{ formatPrice(store.currentPrice) }} €</span>
                </div>
                <div class="cv3-deposit-card cv3-deposit-remains">
                    <span class="cv3-deposit-label">{{ $t('checkout.v3.remaining_to_pay_label') || 'Reste à payer' }}</span>
                    <span class="cv3-deposit-amount">{{ formatPrice(restePanier) }} €</span>
                </div>
            </div>
            <!-- Vente clôturée : lecture seule totale. Le bouton n'ouvre qu'un récapitulatif en lecture
                 seule (ni encaissement, ni remboursement — les avoirs se font depuis la page factures). -->
            <button
                type="button"
                class="cv3-encaisser"
                :disabled="!store.count || store.busy || hasPendingVoucherDraft"
                :title="hasPendingVoucherDraft ? $t('checkout.v3.validate_voucher_first') : ''"
                @click="$emit('encaisser')"
            >
                {{ locked ? $t('checkout.v3.view_btn') : $t('checkout.v3.cash_in_btn') }}
            </button>
            <!-- Un bouton mort sans explication est pire que pas de garde : on dit pourquoi. -->
            <p v-if="hasPendingVoucherDraft" class="cv3-encaisser-hint">
                {{ $t('checkout.v3.validate_voucher_first') }}
            </p>
            <div class="cv3-cart-foot-secondary">
                <button
                    type="button"
                    class="cv3-secondary"
                    :disabled="!store.count || store.busy || locked"
                    @click="$emit('hold')"
                >
                    {{ $t('checkout.v3.on_hold_btn') }}
                </button>
                <!-- Volontairement PAS verrouillé : c'est la sortie de secours pour repartir sur une
                     nouvelle vente depuis une vente clôturée ouverte en lecture seule. -->
                <button
                    type="button"
                    class="cv3-secondary cv3-annuler"
                    :disabled="!store.count || store.busy"
                    @click="$emit('new-sale')"
                >
                    {{ locked ? $t('checkout.v3.new_sale_btn') : $t('checkout.v3.cancel_btn') || 'Annuler' }}
                </button>
            </div>
        </footer>

        <VoucherValidationModal
            :store="store"
            :open="voucherModalOpen"
            @close="voucherModalOpen = false"
            @validated="voucherModalOpen = false"
        />

        <NoteModal
            :store="store"
            :open="note.open"
            :title="note.title"
            :value="note.value"
            @close="note.open = false"
            @save="saveNote"
        />
    </aside>
</template>

<script>
import {
    MessageCircle as MessageCircleIcon,
    Package as PackageIcon,
    Trash2 as Trash2Icon,
    Clock as ClockIcon,
    Gift as GiftIcon,
    Lock as LockIcon,
    User as UserIcon,
    UserCheck as UserCheckIcon,
    Wallet as WalletIcon,
    Tag as TagIcon,
} from 'lucide-vue'
import QuantityStepper from './QuantityStepper.vue'
import VoucherValidationModal from './VoucherValidationModal.vue'
import NoteModal from './NoteModal.vue'

export default {
    name: 'CartPanel',
    components: {
        MessageCircleIcon,
        PackageIcon,
        Trash2Icon,
        ClockIcon,
        GiftIcon,
        LockIcon,
        UserIcon,
        UserCheckIcon,
        WalletIcon,
        TagIcon,
        QuantityStepper,
        VoucherValidationModal,
        NoteModal,
    },
    props: { store: { type: Object, required: true } },
    data() {
        return {
            voucherModalOpen: false,
            // Note editor (global order note + per-line note). mode = 'order' | 'line'.
            note: { open: false, mode: 'order', line: null, value: '', title: '' },
        }
    },
    computed: {
        // Vente clôturée (status_id 7 / signée) : document fiscal figé, lecture seule (NF525).
        locked() {
            return !!this.store.isClotured
        },
        // Net of any persisted promotion. Falls back to the gross total when there is none
        // (totalNet === total in that case, but stay defensive against an older payload).
        netTotal() {
            return this.store.discountTotal > 0 ? this.store.totalNet : this.store.total
        },
        // What is still owed. Must be computed on the NET total, otherwise a fully-paid discounted
        // order shows its discount as an outstanding balance (54.90 - 46.66 = 8.24 € "still due").
        // Same rule as OrderService::isPaymentComplete(): total_price - remise_price == amount paid.
        restePanier() {
            return Math.max(0, Math.round((this.netTotal - this.store.currentPrice) * 100) / 100)
        },
        hasUndatedDraft() {
            return (this.store.drafts || []).some((d) => d.isUndatedTicket)
        },
        // Un brouillon bon cadeau n'est PAS encore un bon : ni code émis, ni prix sur sa ligne de
        // commande (elle reste à 0 €). Encaisser dans cet état ferait payer le montant du panier
        // — le front ajoute `draftTotal` au total dû — contre une ligne à 0 €, et n'émettrait
        // qu'un seul code quelle que soit la quantité (PLPRB-2813). Le serveur refuse aussi la
        // clôture dans cet état : ceci n'est qu'un raccourci d'affichage.
        // `locked` = vente déjà clôturée : le bouton devient « Voir », jamais désactivé.
        hasPendingVoucherDraft() {
            return !this.locked && (this.store.drafts || []).length > 0
        },
    },
    methods: {
        formatPrice(v) {
            return (Math.round((Number(v) || 0) * 100) / 100).toFixed(2).replace('.', ',')
        },
        // A hidden "remise instantanée" (is_special) shows its title, never its generated code.
        promoDisplay(promo) {
            if (!promo) return ''
            return promo.is_special ? promo.title : promo.code
        },
        // Ligne portant un bon (bon cadeau ou billet non daté) déjà validé : quantité verrouillée.
        isVoucherLine(line) {
            return !!line.item_type && line.item_type.endsWith('\\Voucher')
        },
        // Draft label (i18n): "Billet non daté" for undated tickets, "Bon cadeau" otherwise.
        draftLabel(d) {
            return this.$t(d.isUndatedTicket ? 'checkout.voucher.undated_ticket' : 'checkout.voucher.gift_voucher')
        },
        // Per-unit value of a voucher draft. The backend stores `amount` as the LINE total across the
        // quantity (amount = unit * qty), so the unit shown/edited in the cart is amount / qty.
        draftUnit(d) {
            const qty = Number(d.qty) || 1
            return qty > 0 ? Number(d.amount) / qty : Number(d.amount)
        },
        async remove(line) {
            try {
                await this.store.removeLine(line.rowId)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_deleting'))
            }
        },
        async onQty(line, qty) {
            if (qty === Number(line.qty)) return
            try {
                await this.store.setQty(line.rowId, qty)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_changing_quantity'))
            }
        },
        // ── bowling sèche : l'effectif se règle par re-résolution serveur ─────────────────────
        // ± naviguent la LISTE des effectifs vendables (`bowlingSizes`, donnée serveur), comme le
        // popover d'ajout — jamais un compteur libre : les paliers ne sont pas linéaires. La
        // saisie tapée part telle quelle : le serveur tranche (bornes → 422 lisible, palier, prix,
        // durée). Le panier rafraîchi par mutateCart EST la preuve (prix, durée, effectif visibles) :
        // pas de toast de succès, seulement les refus.
        async onBowlingStep(line, dir) {
            const sizes = line.bowlingSizes || []
            const qty = Number(line.qty)
            // Tailles CROISSANTES (serveur) : « suivant » = première taille > effectif courant,
            // « précédent » = dernière < courant — PAR VALEUR, pas par index : l'effectif courant
            // peut ne plus être vendable (paliers édités pendant que le panier est ouvert), et un
            // indexOf à -1 ferait sauter un « + » au PREMIER palier (une partie de 5 retomberait
            // à 1 joueur). Le serveur revalide les bornes de toute façon — ici, on ne choisit
            // que la cible la moins surprenante.
            const next = dir > 0 ? sizes.find((s) => s > qty) : sizes.filter((s) => s < qty).pop()
            if (next === undefined || next === qty) return
            await this.setBookingQty(line, next)
        },
        async onBowlingQty(line, qty) {
            if (qty === Number(line.qty)) return
            await this.setBookingQty(line, qty)
        },
        async setBookingQty(line, participants) {
            try {
                await this.store.setBookingQty(line.rowId, participants)
            } catch (e) {
                let message = (e && e.message) || ''
                if (message === 'CLOSED_SALE') message = this.$t('checkout.v3.closed_readonly')
                this.$toast && this.$toast.error(message || this.$t('checkout.v3.error_changing_quantity'))
            }
        },
        // Durée effective d'une ligne bowling sèche (palier) — même formatage que le popover.
        formatDuration(minutes) {
            const m = Number(minutes) || 0
            if (!m) return ''
            if (m < 60) return `${m} ${this.$t('checkout.v3.unit_m')}`
            const h = Math.floor(m / 60)
            const rest = m % 60
            return rest
                ? `${h}${this.$t('checkout.v3.unit_h')} ${rest}${this.$t('checkout.v3.unit_m')}`
                : `${h}${this.$t('checkout.v3.unit_h')}`
        },
        async onDraftQty(d, qty) {
            if (qty === Number(d.qty)) return
            try {
                await this.store.draftSetQty(d.voucherItemId, qty)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_changing_quantity'))
            }
        },
        async onPrice(line, ev) {
            const price = parseFloat(ev.target.value)
            if (isNaN(price) || price === Number(line.price)) return
            try {
                await this.store.changePrice(line.rowId, price)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_changing_price'))
            }
        },
        async removeDraft(d) {
            try {
                await this.store.deleteDraft(d.voucherItemId)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_deleting'))
            }
        },
        async onDraftPrice(d, ev) {
            // The input holds the per-UNIT value; the backend stores the line total (amount = unit * qty),
            // so send unit * qty to keep that invariant (matches how the qty stepper recomputes amount).
            const unit = parseFloat(ev.target.value)
            if (isNaN(unit) || unit === this.draftUnit(d)) return
            const qty = Number(d.qty) || 1
            try {
                await this.store.changeDraftPrice(d.voucherItemId, unit * qty)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_changing_price'))
            }
        },
        // ---- notes (global order note + per-line note) ----
        openOrderNote() {
            if (!this.store.orderId) {
                this.$toast && this.$toast.info(this.$t('checkout.v3.add_item_first'))
                return
            }
            this.note = {
                open: true,
                mode: 'order',
                line: null,
                value: this.store.orderComment || '',
                title: this.$t('checkout.v3.note_global_title'),
            }
        },
        openLineNote(line) {
            this.note = {
                open: true,
                mode: 'line',
                line,
                value: line.comment || '',
                title: this.$t('checkout.v3.note_line_title', { name: line.name }),
            }
        },
        async saveNote(text) {
            try {
                if (this.note.mode === 'order') await this.store.updateOrderNote(text)
                else await this.store.updateLineNote(this.note.line.rowId, text)
                this.note.open = false
                this.$toast && this.$toast.success(this.$t('checkout.v3.note_saved'))
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_saving_note'))
            }
        },
        // ---- per-line seller / adviser ----
        staffName(s) {
            return [s.first_name, s.last_name].filter(Boolean).join(' ') || '#' + s.id
        },
        async onSeller(line, ev) {
            const sellerId = ev.target.value || null
            try {
                await this.store.editSeller(line.rowId, sellerId, line.advised_id || null)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_assigning_seller'))
            }
        },
        async onAdvised(line, ev) {
            const advisedId = ev.target.value || null
            try {
                await this.store.editSeller(line.rowId, line.seller_id || null, advisedId)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_assigning_adviser'))
            }
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-cart {
    width: 440px;
    flex-shrink: 0;
    background: var(--cv3-aside);
    border-radius: 27px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.cv3-cart-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 19px 19px 14px;
    border-bottom: 1px solid var(--cv3-border);
}
.cv3-cart-title {
    display: flex;
    align-items: center;
    gap: 11px;
    h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 800;
        letter-spacing: -0.16px;
        color: var(--cv3-text);
    }
}
.cv3-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 21px;
    padding: 0 7px;
    border-radius: 999px;
    background: #fff;
    color: #021832;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    font-size: 12px;
}
.cv3-newsale {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 0;
    background: var(--cv3-accent);
    color: #fff;
    font-size: 12px;
    font-weight: 500;
}
.cv3-note-global-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 1px solid var(--cv3-border-input);
    background: rgba(255, 255, 255, 0.05);
    color: var(--cv3-muted);
    flex-shrink: 0;
    transition:
        background 0.15s,
        color 0.15s,
        border-color 0.15s;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: var(--cv3-text);
    }
    &.filled {
        background: rgba(125, 45, 189, 0.2);
        border-color: var(--cv3-accent);
        color: var(--cv3-accent);
    }
}
.cv3-cart-body {
    flex: 1 1 auto;
    overflow: auto;
    padding: 15px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.cv3-cart-empty {
    color: var(--cv3-muted);
    text-align: center;
    padding: 30px 0;
    font-size: 14px;
}
.cv3-line {
    background: var(--cv3-panel);
    border-radius: var(--cv3-radius-card);
    padding: 13px;
    box-shadow: var(--cv3-shadow);
    display: flex;
    flex-direction: column;
    gap: 12px;
}
/* Ligne enfant d'un billet simple : décalée sous son parent, avec un filet de rattachement.
   Purement visuel (parent_id vient du serveur) — aucune règle métier côté front. */
.cv3-line-child {
    margin-left: 22px;
    border-left: 3px solid var(--cv3-icon-tint);
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
}
.cv3-line-top {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
.cv3-line-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 31px;
    height: 31px;
    border-radius: 9px;
    background: var(--cv3-icon-tint);
    color: #fff;
    flex-shrink: 0;
    &.draft {
        background: rgba(234, 179, 8, 0.18);
        color: #facc15;
    }
}
.cv3-draft {
    border: 1px solid rgba(234, 179, 8, 0.25);
}
.cv3-draft-tag {
    align-self: flex-start;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #facc15;
    letter-spacing: 0.4px;
}
.cv3-draft-linetotal {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: var(--cv3-muted);
    span:last-child {
        color: var(--cv3-text);
        font-weight: 700;
    }
}
.cv3-locked-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 9px 12px;
    border-radius: 10px;
    background: rgba(250, 204, 21, 0.1);
    border: 1px solid rgba(250, 204, 21, 0.35);
    color: #facc15;
    font-size: 12px;
    font-weight: 700;
}
.cv3-line-discount {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
    flex-wrap: wrap;
}
.cv3-line-promo {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(74, 222, 128, 0.12);
    border: 1px solid rgba(74, 222, 128, 0.35);
    color: #4ade80;
    font-size: 11px;
    font-weight: 700;
}
.cv3-line-prices {
    display: inline-flex;
    align-items: baseline;
    gap: 7px;
}
.cv3-line-oldprice {
    font-size: 12px;
    font-weight: 600;
    color: var(--cv3-muted);
}
.cv3-line-newprice {
    font-size: 13px;
    font-weight: 700;
    color: var(--cv3-text);
}
.cv3-validate-bc {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    width: 100%;
    padding: 11px;
    border-radius: 11px;
    border: 1px solid rgba(234, 179, 8, 0.4);
    background: rgba(234, 179, 8, 0.12);
    color: #facc15;
    font-weight: 700;
    font-size: 13px;
}
.cv3-line-info {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cv3-line-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--cv3-text);
}
/* Durée effective d'une ligne bowling sèche (palier) — puce discrète sous le nom, donnée
   serveur, rafraîchie avec le prix à chaque changement d'effectif. */
.cv3-line-duration {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
    font-size: 11px;
    font-weight: 700;
    color: var(--cv3-accent-text, #c4b5fd);
    background: rgba(125, 45, 189, 0.18);
    border: 1px solid rgba(171, 110, 220, 0.35);
    border-radius: 999px;
    padding: 2px 8px;
}
.cv3-line-note {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    font-size: 12px;
    font-weight: 600;
    align-self: flex-start;
    max-width: 100%;
    padding: 0;
    text-align: left;
    &.filled {
        color: var(--cv3-accent-text, #c4b5fd);
    }
    .cv3-note-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
.cv3-line-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 29px;
    height: 29px;
    border-radius: 8px;
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    &:hover:not(:disabled) {
        color: #ff6b6b;
        background: rgba(255, 107, 107, 0.1);
    }
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}
.cv3-line-locked {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 29px;
    height: 29px;
    border-radius: 8px;
    color: var(--cv3-muted);
    opacity: 0.7;
    cursor: not-allowed;
}
.cv3-line-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.cv3-line-staff {
    display: flex;
    gap: 8px;
}
.cv3-staff-field {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cv3-staff-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--cv3-muted);
}
.cv3-staff-field select {
    width: 100%;
    min-width: 0;
    padding: 7px 9px;
    border-radius: 9px;
    border: 1px solid var(--cv3-border-input);
    background: var(--cv3-input);
    color: var(--cv3-text);
    font-size: 12px;
    font-weight: 600;
}
.cv3-price-input {
    display: flex;
    align-items: center;
    border-radius: 10px;
    border: 1px solid var(--cv3-border-input);
    background: var(--cv3-input);
    padding: 8px 12px;
    width: 110px;
    input {
        width: 100%;
        background: transparent;
        border: 0;
        outline: 0;
        color: var(--cv3-text);
        font-weight: 700;
        font-size: 14px;
        -moz-appearance: textfield;
        appearance: none;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}
.cv3-cur {
    color: var(--cv3-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    font-size: 13px;
}
.cv3-cart-foot {
    padding: 17px;
    border-top: 1px solid var(--cv3-border);
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.cv3-subtotal-row,
.cv3-remise-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
    color: var(--cv3-muted);
}
.cv3-remise-row span:last-child {
    color: #4ade80;
    font-weight: 700;
}
.cv3-total-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}
.cv3-total-label {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #fff;
}
.cv3-total-val {
    font-size: 25px;
    font-weight: 700;
    letter-spacing: -0.25px;
    color: var(--cv3-text);
}
.cv3-deposit-row {
    display: flex;
    gap: 8px;
}
.cv3-deposit-card {
    flex: 1 1 0;
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.cv3-deposit-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.cv3-deposit-amount {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.3px;
}
.cv3-deposit-paid {
    background: rgba(74, 222, 128, 0.12);
    border: 1px solid rgba(74, 222, 128, 0.2);
    .cv3-deposit-label {
        color: #4ade80;
    }
    .cv3-deposit-amount {
        color: #4ade80;
    }
}
.cv3-deposit-remains {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--cv3-border-input);
    .cv3-deposit-label {
        color: var(--cv3-muted);
    }
    .cv3-deposit-amount {
        color: var(--cv3-text);
    }
}
.cv3-encaisser {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px;
    border-radius: 13px;
    border: 0;
    background: var(--cv3-accent-2);
    color: #fff;
    font-size: 15px;
    font-weight: 500;
    box-shadow: 0 9px 23px -7px rgba(125, 45, 189, 0.7);
    &:disabled {
        box-shadow: none;
        opacity: 0.55;
        cursor: not-allowed;
    }
}
/* Explique pourquoi « Encaisser » est grisé (brouillon bon cadeau non validé). */
.cv3-encaisser-hint {
    margin: 8px 0 0;
    text-align: center;
    font-size: 12px;
    line-height: 1.35;
    color: var(--cv3-warn, #e0b23c);
}
.cv3-cart-foot-secondary {
    display: flex;
    gap: 8px;
    width: 100%;
}
.cv3-cart-foot-secondary .cv3-secondary {
    flex: 1;
}
.cv3-secondary {
    padding: 12px;
    border-radius: 12px;
    border: 1px solid var(--cv3-border-input);
    background: transparent;
    color: var(--cv3-text-2);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    text-align: center;
}
.cv3-annuler {
    border-color: rgba(239, 68, 68, 0.4);
    color: #f87171;
    &:hover:not(:disabled) {
        background: rgba(239, 68, 68, 0.1);
    }
}
.cv3-draft .cv3-line-name {
    color: #facc15;
}
.cv3-draft .cv3-validate-bc {
    margin-top: 12px;
}
</style>
